import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUsersQuery } from '../../../hooks/queries/useUsersQuery';
import { useAuth } from '../../../hooks/useAuth';
import { capitalizeFirstLetter, formatName } from '../../../utils/capitalize';
import { getPriceTypeInfos, localStorageKeys, permissionLevels, systemConfigKeyEnum } from '../../../utils/enums';
import { formatNumber } from '../../../utils/numbers';
import GenericAgGridTable from '../GenericAgGridTable';
import { InheritedPriceDisplay } from '../../InheritedPriceDisplay';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { User } from '../../../services/apiSLTP/users';
import { useSystemConfig } from '../../../hooks/useSystemConfig';

interface UsersPLITableProps {}

const UsersPLITable: React.FC<UsersPLITableProps> = () => {
    const { t } = useTranslation();
    const { user: currentUser } = useAuth();
    const {getConfigValue} = useSystemConfig();

    const { data: dataUsers, isLoading: isUsersLoading } = useUsersQuery({ queries: { active: true } });

    const sortedUsers = () => {
        return (dataUsers || [])
            .filter(
                (user) =>
                    !user.isSpecial &&
                    [permissionLevels.CHAUFFEUR, permissionLevels.CONDUC, permissionLevels.OBSERVERS, permissionLevels.TERRAIN].includes(
                        user.profileId.permsLevel
                    )
            )
            .sort((a, b) => {
                const lastNameA = formatName(a?.firstName, a?.lastName);
                const lastNameB = b?.lastName;
                if (lastNameA < lastNameB) return -1;
                if (lastNameA > lastNameB) return 1;
                const firstNameA = a?.firstName;
                const firstNameB = b?.firstName;
                if (firstNameA < firstNameB) return -1;
                if (firstNameA > firstNameB) return 1;
                return 0;
            });
    };

    const columnDefs: ColDef<User>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            valueGetter: (params) => (params.data ? formatName(params.data.firstName, params.data.lastName) : ''),
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('price') + ` (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`),
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            valueGetter: (params) => params.data?.price ?? params.data?.jobId?.price,
            cellRenderer: (params: ICellRendererParams<User>) =>
                params.data ? (
                    <InheritedPriceDisplay
                        directValue={params.data.price}
                        inheritedValue={params.data.jobId?.price}
                        format={(value) => formatNumber(value, t('locale'))}
                    />
                ) : params.value,
            colId: 'price',
        },
        {
            headerName: capitalizeFirstLetter(t('pricing')),
            valueGetter: (params) => (params.data ? t(getPriceTypeInfos(params.data.priceType ?? params.data.jobId?.priceType)) : ''),
            cellRenderer: (params: ICellRendererParams<User>) =>
                params.data ? (
                    <InheritedPriceDisplay
                        directValue={params.data.priceType}
                        inheritedValue={params.data.jobId?.priceType}
                        format={(value) => capitalizeFirstLetter(t(getPriceTypeInfos(value)))}
                    />
                ) : (
                    params.value
                ),
            colId: 'priceType',
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'codeProC',
            colId: 'codeProC',
        },
        {
            headerName: capitalizeFirstLetter(t('job')),
            field: 'jobId.designation',
            cellRenderer: (params: ICellRendererParams<User>) => params.value,
            colId: 'job',
        },
        ...((currentUser?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                      minWidth: 250,
                      colId: '_id',
                  },
              ]
            : []) as ColDef<User>[]),
    ];

    return (
        <GenericAgGridTable
            rowData={sortedUsers()}
            columnDefs={columnDefs}
            loading={isUsersLoading}
            exportCsvFileName="sales-pli-users"
            storageKey={localStorageKeys.TABLE.PLI.USERS}
            agGridProps={{ getRowId: ({ data }) => data._id }}
        />
    );
};

export default UsersPLITable;
